import { cn } from 'cn';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  JANELA_PENDENCIAS_DIAS,
  statusRegistro,
  type RegistroResumoOutputType,
  type StatusRegistro,
} from '@shared/dto';

import { HistoricoDialog } from '../../components/common/historico-dialog';
import { MultiSelectCombobox } from '../../components/common/multi-select-combobox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { useApiQuery } from '../../hooks/use-api';
import { materiasApi } from '../../api/materias.api';
import { professoresApi } from '../../api/professores.api';
import { registrosApi } from '../../api/registros.api';
import { RegistrarAulaDialog } from './components/registrar-aula-dialog';
import { RegistroRow } from './components/registro-row';

function paraISO(data: Date): string {
  return data.toISOString().slice(0, 10);
}

/** `Date` no tipo, mas `fetch`+`JSON.parse` nunca revive datas -- valor real em runtime é string ISO. */
function paraISOFlexivel(valor: Date | string): string {
  return paraISO(valor instanceof Date ? valor : new Date(valor));
}

/** "26/08 (qua)" -- opções do seletor "ver dia" do aviso de pendências. */
function formatarDataCurta(dataISO: string): string {
  const data = new Date(`${dataISO}T00:00:00.000Z`);
  const dia = data.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  });
  const semana = data
    .toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'UTC' })
    .replace('.', '');
  return `${dia} (${semana})`;
}

/** Ordem de exibição dos chips -- Concluídos primeiro, Não iniciados por último. */
const ORDEM_STATUS: StatusRegistro[] = ['CONCLUIDO', 'PENDENTE', 'NAO_INICIADO'];

const CHIP_LABEL_PLURAL: Record<StatusRegistro, string> = {
  CONCLUIDO: 'Concluídos',
  PENDENTE: 'Pendentes',
  NAO_INICIADO: 'Não iniciados',
};

const CHIP_CLASSES: Record<StatusRegistro, string> = {
  CONCLUIDO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PENDENTE: 'bg-orange-50 text-orange-700 border-orange-200',
  NAO_INICIADO: 'bg-slate-100 text-slate-600 border-slate-200',
};

export function AcompanhamentoPage() {
  const [data, setData] = useState(() => paraISO(new Date()));
  const [filtroStatus, setFiltroStatus] = useState<StatusRegistro | null>(null);
  const [professorIds, setProfessorIds] = useState<string[]>([]);
  const [alunoIds, setAlunoIds] = useState<string[]>([]);
  const [horarios, setHorarios] = useState<string[]>([]);
  const [linhaAtiva, setLinhaAtiva] = useState<RegistroResumoOutputType | null>(null);
  const [alunoHistorico, setAlunoHistorico] = useState<{ id: string; nome: string } | null>(null);

  const {
    data: registros,
    loading,
    refetch,
  } = useApiQuery(registrosApi.listarDoDia, { data }, { keepPreviousData: true });
  const { data: professores } = useApiQuery(professoresApi.listar, undefined);
  const { data: materias } = useApiQuery(materiasApi.listar, undefined);
  // Independente da `data` selecionada: "atrasado" é sempre relativo a hoje de verdade.
  const { data: pendencias } = useApiQuery(registrosApi.contarPendencias, undefined);

  // Só o que existe nos registros do dia: não faz sentido oferecer professor ou horário sem aula hoje.
  const opcoesProfessor = useMemo(() => {
    const idsNoDia = new Set((registros ?? []).map((r) => r.professorId));
    return (professores ?? [])
      .filter((p) => idsNoDia.has(p.id))
      .map((p) => ({ value: p.id, label: p.nome }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [registros, professores]);

  const opcoesAluno = useMemo(() => {
    const nomePorId = new Map((registros ?? []).map((r) => [r.alunoId, r.alunoNome]));
    return [...nomePorId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [registros]);

  const opcoesHorario = useMemo(() => {
    const unicos = [...new Set((registros ?? []).map((r) => r.horarioPrevisto))].sort();
    return unicos.map((h) => ({ value: h, label: h }));
  }, [registros]);

  // Filtrado por Professor/Aluno/Horário; base das contagens dos chips, que seguem mostrando os 3 estados mesmo com um chip selecionado.
  const registrosBuscados = useMemo(() => {
    return (registros ?? []).filter((r) => {
      if (professorIds.length > 0 && !professorIds.includes(r.professorId)) {
        return false;
      }
      if (alunoIds.length > 0 && !alunoIds.includes(r.alunoId)) {
        return false;
      }
      if (horarios.length > 0 && !horarios.includes(r.horarioPrevisto)) {
        return false;
      }
      return true;
    });
  }, [registros, professorIds, alunoIds, horarios]);

  const registrosFiltrados = useMemo(() => {
    if (!filtroStatus) {
      return registrosBuscados;
    }
    return registrosBuscados.filter((r) => statusRegistro(r) === filtroStatus);
  }, [registrosBuscados, filtroStatus]);

  // Um grupo por horário: o cabeçalho mostra o horário uma vez só.
  const gruposPorHorario = useMemo(() => {
    const ordenados = [...registrosFiltrados].sort((a, b) =>
      a.horarioPrevisto.localeCompare(b.horarioPrevisto),
    );
    const grupos = new Map<string, RegistroResumoOutputType[]>();
    for (const registro of ordenados) {
      const lista = grupos.get(registro.horarioPrevisto) ?? [];
      lista.push(registro);
      grupos.set(registro.horarioPrevisto, lista);
    }
    return [...grupos.entries()];
  }, [registrosFiltrados]);

  const contagens = useMemo(() => {
    const base: Record<StatusRegistro, number> = {
      NAO_INICIADO: 0,
      PENDENTE: 0,
      CONCLUIDO: 0,
    };
    for (const registro of registrosBuscados) {
      base[statusRegistro(registro)] += 1;
    }
    return base;
  }, [registrosBuscados]);

  // Só a escrita fica bloqueada numa data futura; navegar continua liberado.
  const bloqueadoFuturo = data > paraISO(new Date());

  function mudarDia(deltaDias: number) {
    const atual = new Date(`${data}T00:00:00`);
    atual.setDate(atual.getDate() + deltaDias);
    setData(paraISO(atual));
  }

  // Pula pro dia escolhido e limpa os outros filtros, senão um filtro ligado esconderia o que o aviso prometeu mostrar.
  function irParaData(dataISO: string) {
    setData(dataISO);
    setFiltroStatus(null);
    setProfessorIds([]);
    setAlunoIds([]);
    setHorarios([]);
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Acompanhamento</h1>
        <p className="text-sm text-muted-foreground">Lista diária de aulas por horário</p>
      </div>

      {pendencias && pendencias.total > 0 ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-sm text-orange-700">
          <AlertTriangle className="size-4 shrink-0" />
          <span>
            <strong className="font-semibold">{pendencias.total}</strong>{' '}
            {pendencias.total === 1
              ? 'acompanhamento não finalizado'
              : 'acompanhamentos não finalizados'}{' '}
            nos últimos {JANELA_PENDENCIAS_DIAS} dias.
          </span>
          <Select onValueChange={irParaData}>
            <SelectTrigger className="ml-auto w-auto border-orange-300 bg-white text-xs" size="sm">
              <SelectValue placeholder="Ver dia..." />
            </SelectTrigger>
            <SelectContent>
              {pendencias.datas.map((dataPendencia) => {
                const dataISO = paraISOFlexivel(dataPendencia);
                return (
                  <SelectItem key={dataISO} value={dataISO}>
                    {formatarDataCurta(dataISO)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => mudarDia(-1)}>
          <ChevronLeft className="size-4" />
        </Button>
        <Input
          type="date"
          value={data}
          onChange={(e) => setData(e.target.value)}
          className="w-auto"
        />
        <Button variant="outline" size="icon" onClick={() => mudarDia(1)}>
          <ChevronRight className="size-4" />
        </Button>

        <div className="flex flex-wrap gap-2">
          {ORDEM_STATUS.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setFiltroStatus((atual) => (atual === status ? null : status))}
              className={cn(
                'rounded-xl border px-3 py-2 text-left transition-all hover:brightness-95',
                CHIP_CLASSES[status],
                filtroStatus === status ? 'ring-2 ring-offset-1' : null,
              )}
            >
              <span className="mr-1.5 text-lg font-semibold">{contagens[status]}</span>
              <span className="text-xs">{CHIP_LABEL_PLURAL[status]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <MultiSelectCombobox
          className="w-48"
          placeholder="Professor"
          searchPlaceholder="Buscar professor..."
          options={opcoesProfessor}
          value={professorIds}
          onValueChange={setProfessorIds}
        />
        <MultiSelectCombobox
          className="w-40"
          placeholder="Aluno"
          searchPlaceholder="Buscar aluno..."
          options={opcoesAluno}
          value={alunoIds}
          onValueChange={setAlunoIds}
        />
        <MultiSelectCombobox
          className="w-32"
          placeholder="Horário"
          options={opcoesHorario}
          value={horarios}
          onValueChange={setHorarios}
        />
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : null}

      {!loading && registrosFiltrados.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {alunoIds.length > 0 || professorIds.length > 0 || horarios.length > 0
            ? 'Nenhum registro encontrado para esse filtro.'
            : 'Nenhuma aula neste dia.'}
        </p>
      ) : null}

      <div className="space-y-4">
        {gruposPorHorario.map(([horario, registrosDoHorario]) => (
          <div key={horario}>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-sm font-semibold text-muted-foreground">{horario}</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="space-y-2">
              {registrosDoHorario.map((registro) => (
                <RegistroRow
                  key={registro.horarioId}
                  registro={registro}
                  bloqueadoFuturo={bloqueadoFuturo}
                  professores={professores ?? []}
                  materias={materias ?? []}
                  onRegistrarAula={() => setLinhaAtiva(registro)}
                  onVerHistorico={() =>
                    setAlunoHistorico({ id: registro.alunoId, nome: registro.alunoNome })
                  }
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <RegistrarAulaDialog
        key={linhaAtiva?.horarioId ?? 'fechado'}
        open={Boolean(linhaAtiva)}
        onOpenChange={(open) => !open && setLinhaAtiva(null)}
        resumo={linhaAtiva}
        bloqueadoFuturo={bloqueadoFuturo}
        onSalvo={refetch}
        professores={professores ?? []}
        materias={materias ?? []}
      />

      <HistoricoDialog
        open={Boolean(alunoHistorico)}
        onOpenChange={(open) => !open && setAlunoHistorico(null)}
        alunoId={alunoHistorico?.id ?? null}
        alunoNome={alunoHistorico?.nome ?? ''}
      />
    </div>
  );
}
